import type { ComponentProps, ReactNode } from "react"

import type { LucideIcon } from "@/components/icons"

import { cn } from "@/lib/utils"

type LaunchTileProps = Omit<ComponentProps<"button">, "title"> & {
  /** Lucide icon, rendered inside the canonical primary-tinted square. */
  icon: LucideIcon
  title: string
  description?: string
  /** Vertical = icon-over-text (centered); horizontal = icon-left, text-right. */
  orientation?: "vertical" | "horizontal"
  /** `primary` tints the whole tile as a highlighted CTA (e.g. "AI Quick Start"). */
  emphasis?: "default" | "primary"
  /** Selected state — border + tint, matching the module gallery's active card. */
  active?: boolean
  /** Optional trailing content next to the title (badge, check, count). */
  trailing?: ReactNode
}

/**
 * Shared "launch tile" — the canonical card used to launch a module or action.
 * Single source of truth for the treatment that the module gallery established
 * (`rounded-lg` card, `size-10 rounded-lg` primary-tinted icon square, gentle
 * lift on hover, `active:scale-[0.98]`). Landing pages should use this instead
 * of hand-rolling their own tiles so radius, icon-container shape, sizing, and
 * hover accent stay consistent across modules (see DESIGN.md).
 */
export function LaunchTile({
  icon: Icon,
  title,
  description,
  orientation = "vertical",
  emphasis = "default",
  active = false,
  trailing,
  className,
  ...rest
}: LaunchTileProps) {
  const isHorizontal = orientation === "horizontal"

  return (
    <button
      type="button"
      className={cn(
        "card-interactive group bg-card text-card-foreground flex gap-3 rounded-lg border p-4 shadow-sm",
        "active:scale-[0.98]",
        "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
        isHorizontal ? "items-center text-left" : "flex-col items-center text-center",
        emphasis === "primary" &&
          "border-primary/30 bg-primary/5 hover:border-primary/50 hover:bg-primary/10",
        active && "border-primary/40 bg-primary/5",
        className
      )}
      {...rest}
    >
      <div className="bg-primary/10 text-primary group-hover:bg-primary/20 flex size-10 shrink-0 items-center justify-center rounded-lg transition-colors duration-200">
        <Icon className="size-5" />
      </div>
      <div className={cn("min-w-0", isHorizontal && "flex-1")}>
        <div className={cn("flex items-center gap-2", !isHorizontal && "justify-center")}>
          <p className="text-sm font-semibold">{title}</p>
          {trailing}
        </div>
        {description && <p className="text-muted-foreground mt-0.5 text-xs">{description}</p>}
      </div>
    </button>
  )
}
